/**
 * Generic formatting helpers (dates, text). Currency/TON formatting lives
 * in `lib/ton/format.ts` because it must always operate on bigint nanotons.
 */

const DATE_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
});

export function formatDate(input: string | number | Date): string {
  const d = input instanceof Date ? input : new Date(input);
  return DATE_FMT.format(d);
}

export function formatDateTime(input: string | number | Date): string {
  const d = input instanceof Date ? input : new Date(input);
  return DATE_TIME_FMT.format(d);
}

/**
 * Return a short relative time label ("2m ago", "in 3h"). Intentionally
 * locale-unaware for now — Wave B will switch to `Intl.RelativeTimeFormat`
 * with the active locale from the i18n provider.
 */
export function formatRelativeTime(input: string | number | Date): string {
  const target = input instanceof Date ? input.getTime() : new Date(input).getTime();
  const diffMs = target - Date.now();
  const absMs = Math.abs(diffMs);
  const sign = diffMs >= 0 ? 1 : -1;

  const units: Array<{ ms: number; label: string }> = [
    { ms: 1000 * 60 * 60 * 24 * 365, label: 'y' },
    { ms: 1000 * 60 * 60 * 24 * 30, label: 'mo' },
    { ms: 1000 * 60 * 60 * 24, label: 'd' },
    { ms: 1000 * 60 * 60, label: 'h' },
    { ms: 1000 * 60, label: 'm' },
    { ms: 1000, label: 's' },
  ];

  for (const unit of units) {
    if (absMs >= unit.ms) {
      const value = Math.round(absMs / unit.ms);
      return sign < 0 ? `${value}${unit.label} ago` : `in ${value}${unit.label}`;
    }
  }
  return 'now';
}

export function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1))}…`;
}

/**
 * Compact "time remaining" formatter for list rows and badges.
 *
 * Returns a small object with the rendered text PLUS the raw remaining
 * milliseconds and an `expired` flag, so the caller can also use the
 * value to drive colour staging without parsing the string.
 *
 * Format examples (Turkish-style abbreviations the rest of the app uses):
 *   - 2 days, 3 hours left  → "2g 3s kaldi"
 *   - 0 days, 5 hours, 12 mins → "5s 12d kaldi"
 *   - 0 hours, 35 mins → "35d kaldi"
 *   - 0 mins, 40 sec → "<1d kaldi"     ← Ferdi's explicit request
 *   - past deadline → "Sure doldu"
 *   - missing/invalid input → "—"
 */
export function formatRemainingShort(
  deadlineIso: string | null | undefined,
  now: number = Date.now(),
): { text: string; remainingMs: number; expired: boolean } {
  if (!deadlineIso) {
    return { text: '—', remainingMs: 0, expired: false };
  }
  const deadlineMs = new Date(deadlineIso).getTime();
  if (Number.isNaN(deadlineMs)) {
    return { text: '—', remainingMs: 0, expired: false };
  }
  const remainingMs = deadlineMs - now;
  if (remainingMs <= 0) {
    return { text: 'Sure doldu', remainingMs: 0, expired: true };
  }

  const totalSec = Math.floor(remainingMs / 1000);
  if (totalSec < 60) {
    return { text: '<1d kaldi', remainingMs, expired: false };
  }
  const days = Math.floor(totalSec / 86_400);
  const hours = Math.floor((totalSec % 86_400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return { text: `${days}g ${hours}s kaldi`, remainingMs, expired: false };
  if (hours > 0) return { text: `${hours}s ${mins}d kaldi`, remainingMs, expired: false };
  return { text: `${mins}d kaldi`, remainingMs, expired: false };
}
